import { useEffect, useState } from "react";
import api, { apiError } from "../../lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  "Sourcing": "text-muted-s",
  "In discussion": "text-tint",
  "Coming soon": "!text-[#E8E6E1]",
};

// Valuation is stored in USD millions. Format numeric values with M / B; pass text through unchanged.
function fmtValuation(v) {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  if (!s) return "";
  if (!/\d/.test(s) || !/^[\d,.\s]+$/.test(s)) return s;
  const num = Number(s.replace(/[,\s]/g, ""));
  if (isNaN(num)) return s;
  const clean = (n) => (n % 1 === 0 ? n.toFixed(0) : n.toFixed(1).replace(/\.0$/, ""));
  return num >= 1000 ? `$${clean(num / 1000)}B` : `$${clean(num)}M`;
}

export default function Pipeline() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/pipeline").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const toggle = async (c) => {
    setBusy(c.pipeline_id);
    try {
      if (c.my_interest) {
        await api.delete(`/pipeline/${c.pipeline_id}/interest`);
        setItems((xs) => xs.map((x) => (x.pipeline_id === c.pipeline_id ? { ...x, my_interest: false } : x)));
      } else {
        await api.post(`/pipeline/${c.pipeline_id}/interest`);
        setItems((xs) => xs.map((x) => (x.pipeline_id === c.pipeline_id ? { ...x, my_interest: true } : x)));
        toast.success(`We will let you know first when ${c.name} opens.`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  if (!items) return <span className="t-label">Loading</span>;

  return (
    <div className="rise" data-testid="pipeline-page">
      <span className="t-label">Pipeline</span>
      <h1 className="t-h1 text-bone mt-3">Companies we are pursuing</h1>
      <p className="t-small text-muted-s mt-2 measure">
        An early look at the names Solera is working to bring to the platform. Register interest to be
        notified first when one opens for allocation. This is a preview, not an offer.
      </p>

      <div className="mt-10 space-y-0">
        {items.map((c) => (
          <div key={c.pipeline_id} className="hairline py-6 grid grid-cols-1 gap-y-4 md:grid-cols-[minmax(0,1fr)_200px_170px] md:gap-x-10 md:items-start" data-testid={`pipeline-card-${c.pipeline_id}`}>
            <div className="min-w-0">
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="t-body text-bone">{c.name}</span>
                <span className={`t-label ${STATUS_STYLE[c.status] || "text-muted-s"}`} data-testid={`pipeline-status-${c.pipeline_id}`}>{c.status}</span>
                {c.private && <span className="t-label !text-[#C69A4E]" data-testid={`pipeline-private-${c.pipeline_id}`}>Invite only</span>}
              </div>
              {c.theme && <div className="t-cap text-muted-s mt-1">{c.theme}</div>}
              {c.latest_round && <p className="t-small text-muted-s mt-2 measure">{c.latest_round}</p>}
            </div>
            <div className="t-small">
              {fmtValuation(c.valuation) && <div className="fig text-bone">{fmtValuation(c.valuation)}</div>}
              {c.round_stage && <div className="t-cap text-muted-s mt-1">{c.round_stage}</div>}
              {c.round_date && <div className="t-cap text-muted-s mt-1">{c.round_date}</div>}
              {c.source_url && (
                <a href={c.source_url} target="_blank" rel="noreferrer" className="t-cap text-muted-s hover:!text-[#E8E6E1] underline underline-offset-2 mt-1 inline-block" data-testid={`pipeline-website-${c.pipeline_id}`}>Source</a>
              )}
            </div>
            <button onClick={() => toggle(c)} disabled={busy === c.pipeline_id}
              className={`t-label px-3 py-1.5 border transition-colors duration-150 whitespace-nowrap self-start justify-self-start md:justify-self-end h-fit ${c.my_interest ? "border-[#C69A4E] !text-[#C69A4E]" : "border-[#24272B] hover:!text-[#E8E6E1]"}`}
              data-testid={`pipeline-interest-${c.pipeline_id}`}>
              {c.my_interest ? "Interested" : "Register interest"}
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="t-small text-muted-s hairline py-8" data-testid="pipeline-empty">No companies to preview yet. Check back soon.</p>}
      </div>
    </div>
  );
}
